import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { paths } from '@app/constants';
import { useToggleState } from '@app/utils';
import { Namespace } from '@data/utils/enums';
import { AntdCol, AntdGrid, AntdModal, AntdSpace, AntdTypography } from '@ui/components';
import { CurewikiLogoDakTheme } from '@ui/icons';

const { Title, Text } = AntdTypography;
const { useBreakpoint } = AntdGrid;

type HintProps = {
  showDescription?: boolean;
};

export const Hint = ({ showDescription }: HintProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { md } = useBreakpoint();
  const [bulletPointModalIsOpen, toggleBulletPointModalIsOpen] = useToggleState(false);

  const bulletPoints = t('sign_up.hint.bullet_points', {
    returnObjects: true,
  }) as { description: string; title: string }[];

  return (
    <>
      <AntdCol sm={24} md={14} className="bg-primary-600 w-full p-4 md:h-screen md:overflow-y-auto">
        <AntdSpace orientation="horizontal" className="justify-between w-full">
          <CurewikiLogoDakTheme height={40} />
        </AntdSpace>
        <div className="flex flex-col w-full md:px-12 md:mt-14 lg:px-20 md:h-[calc(100vh-140px)]">
          {(showDescription || md) && (
            <>
              <Title level={1} className="text-base-100 text-2xl md:text-3xl m-0 mt-4">
                {t('sign_up.hint.title')}
              </Title>
              <Text className="text-base-100 text-xs md:text-sm mt-2 md:mt-6">
                <Trans
                  t={t}
                  i18nKey="sign_up.hint.description"
                  components={[
                    <Text className="text-base-100 text-xs md:hidden" key="highlight" />,
                    <Text
                      className="text-base-100 text-xs  md:hidden underline cursor-pointer"
                      key="highlight"
                      onClick={toggleBulletPointModalIsOpen}
                    />,
                  ]}
                />
              </Text>
            </>
          )}
          <ul className="hidden md:flex flex-col gap-6 list-none p-0 mt-6 mb-12">
            {bulletPoints.map((point, index) => (
              <li key={index} className="flex items-center gap-3">
                <div className="text-base-100 text-base rounded-full border border-solid border-base-100 h-[44px] w-[44px] min-h-[44px] min-w-[44px] flex items-center justify-center">
                  {index + 1}
                </div>
                <div className="flex flex-col gap-1">
                  <Text className="text-base-100 text-xl font-semibold">{point.title}</Text>
                  <Text className="text-base-100 text-xs">{point.description}</Text>
                </div>
              </li>
            ))}
          </ul>
          <div className="gap-2 hidden md:flex flex-col border-x-0 border-b-0 border-t border-solid border-primary-300 py-6 mt-auto">
            <Text className="text-base-100 text-base font-bold">
              {t('sign_up.hint.are_you_a_researcher')}
            </Text>
            <Text className="text-base-100 text-xs">
              <Trans
                t={t}
                i18nKey="sign_up.hint.discover"
                components={[
                  <Link to={`/${paths.sign_up}`} key="link" className="underline text-base-100" />,
                ]}
              />
            </Text>
          </div>
        </div>
      </AntdCol>
      <AntdModal
        open={bulletPointModalIsOpen}
        onCancel={toggleBulletPointModalIsOpen}
        footer={null}
      >
        <ul className="flex flex-col gap-6 list-none p-4">
          {bulletPoints.map((point, index) => (
            <li key={index} className="flex items-center gap-3">
              <div className="text-base-100 text-base bg-primary-600 rounded-full border border-solid border-base-100 h-[44px] w-[44px] min-h-[44px] min-w-[44px] flex items-center justify-center">
                {index + 1}
              </div>
              <div className="flex flex-col gap-1">
                <Text className="text-sm font-semibold">{point.title}</Text>
                <Text className="text-sm">{point.description}</Text>
              </div>
            </li>
          ))}
        </ul>
      </AntdModal>
    </>
  );
};
